import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ArrowLeft, Check, ChevronDown, ChevronRight, Loader } from "lucide-react";
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FilterSchemaTile } from "../schema-tile";
import { containsFilter, OPERATOR_LABELS } from "../tree-utils";
import type { FilterOperator, FilterSchema, SchemaRelation } from "../types";
import { loadFilterSchema } from "../use-filter-schema";
import { CHECK_DELAY, checkFormula, formulaFunctions, rememberFunctions, useFormulaScopes, type FormulaCheck, type FormulaContext, type FormulaFunction, type FormulaScope } from "./api";
import { applyCompletion, completeAt, fold, formulaFields, formulaRelations, shownLabel, type Completion, type CompletionItem } from "./completion";
import { FormulaText } from "./formula-text";
import { describeScope, fieldStyle } from "./style";

const BOX = "whitespace-pre-wrap break-words px-2.5 py-2 font-mono text-[13px] leading-[22px] [overflow-wrap:anywhere]";

/** An example of what to write, for the compared field. */
function placeholderFor(label: string, type?: string): string {
    const field = `[${label.split(" › ").pop() || label}]`;
    if (type === "date" || type === "datetime") return `For example ${field} + 10 days, or LATEST(${field}, TODAY())`;
    if (type === "number") return `For example ${field} × 0.5 + 100`;
    return `For example ${field}`;
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

type Selection = { start: number; end: number };

/**
 * The formula a condition compares its field with, edited as text in a popover: coloured as it
 * is typed, completed at the cursor, checked by the server (syntax, fields, types) and tried on a
 * real record. « Done » only takes a valid formula.
 */
export function FormulaEditor({
    context,
    field,
    fieldLabel,
    fieldType,
    operator,
    formula,
    formulaLabel,
    onDone,
    onCancel,
    onManual
}: {
    context: FormulaContext;
    /** The condition's field path, its label for the title, its type for the suggestions. */
    field: string;
    fieldLabel: string;
    fieldType?: string;
    operator: FilterOperator;
    /** The saved formula (canonical) and how it was last shown, when there is one. */
    formula?: string | null;
    formulaLabel?: string | null;
    onDone: (formula: { canonical: string; display: string }) => void;
    onCancel: () => void;
    onManual: () => void;
}) {
    const [text, setText] = useState(formulaLabel ?? "");
    const [selection, setSelection] = useState<Selection>({ start: (formulaLabel ?? "").length, end: (formulaLabel ?? "").length });
    const [check, setCheck] = useState<{ text: string; result: FormulaCheck } | null>(null);
    const [failed, setFailed] = useState(false);
    const [active, setActive] = useState(0);
    const [dismissed, setDismissed] = useState<string | null>(null);
    const [schemas, setSchemas] = useState<Record<string, FilterSchema>>({});
    const [menu, setMenu] = useState<"fields" | "functions" | null>(null);
    const textarea = useRef<HTMLTextAreaElement>(null);
    const marker = useRef<HTMLSpanElement>(null);
    const box = useRef<HTMLDivElement>(null);
    const [anchor, setAnchor] = useState<{ top: number; left: number }>({ top: 30, left: 0 });
    const listId = useId();
    const scopeSchemas = useFormulaScopes(context);
    const scopes = useMemo(() => check?.result.scopes ?? scopeSchemas?.map(item => item.scope) ?? [], [check, scopeSchemas]);
    const functions = check?.result.functions?.length ? check.result.functions : formulaFunctions();
    const functionNames = useMemo(() => new Set(functions.map(item => item.name)), [functions]);

    // The saved formula comes back with today's labels: shown as soon as the server answers.
    const reloaded = useRef(false);
    useEffect(() => {
        if (!formula) return;
        let cancelled = false;
        checkFormula({ ...context, field, operator, formula })
            .then(result => {
                if (cancelled) return;
                rememberFunctions(result.functions);
                reloaded.current = true;
                setText(previous => (previous === (formulaLabel ?? "") ? result.display : previous));
                // An answer for what is being typed may already be in: it stays.
                setCheck(previous => previous ?? { text: result.display, result });
            })
            .catch(() => !cancelled && setFailed(true));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (check?.text === text) return;
        if (formula && !reloaded.current && text === (formulaLabel ?? "")) return;
        let cancelled = false;
        const timer = window.setTimeout(
            () => {
                checkFormula({ ...context, field, operator, text })
                    .then(result => {
                        if (cancelled) return;
                        rememberFunctions(result.functions);
                        setFailed(false);
                        setCheck({ text, result });
                    })
                    .catch(() => !cancelled && setFailed(true));
            },
            // The first check right away: the status, the scopes and the functions show at once.
            check ? CHECK_DELAY : 0
        );
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);

    useEffect(() => {
        const element = textarea.current;
        if (!element) return;
        element.focus();
        element.setSelectionRange(selection.start, selection.end);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const schemaOf = useCallback((model: string) => schemas[model], [schemas]);
    const completion: (Completion & { needs: string[] }) | null = useMemo(() => {
        if (!scopeSchemas || selection.start !== selection.end) return null;
        return completeAt(text, selection.start, scopeSchemas, functions, schemaOf, fieldType);
    }, [text, selection, scopeSchemas, functions, schemaOf, fieldType]);
    const needs = completion?.needs.join("|") ?? "";
    useEffect(() => {
        if (!needs) return;
        needs.split("|").forEach(model => loadFilterSchema(model).then(schema => setSchemas(previous => ({ ...previous, [model]: schema }))));
    }, [needs]);

    const completionKey = completion ? `${text}|${completion.from}` : "";
    const showCompletion = Boolean(completion && completion.items.length > 0 && dismissed !== completionKey && menu === null);
    useEffect(() => setActive(0), [completionKey]);
    useEffect(() => {
        if (!showCompletion || !marker.current || !box.current) return;
        const width = box.current.clientWidth;
        setAnchor({ top: marker.current.offsetTop + 24, left: Math.max(0, Math.min(marker.current.offsetLeft - 2, width - 320)) });
    }, [showCompletion, completionKey]);

    const current = check && check.text === text ? check.result : null;
    const shown = current ?? check?.result ?? null;
    const pending = !current && !failed;
    const valid = current?.status === "valid";
    const errors = current?.errors ?? [];

    // Where the cursor goes once an insertion is in the textarea: set as soon as React wrote the
    // text, before the next keystroke can land elsewhere.
    const pendingCaret = useRef<number | null>(null);
    const write = (next: string, caret: number) => {
        pendingCaret.current = caret;
        setText(next);
        setSelection({ start: caret, end: caret });
    };
    useLayoutEffect(() => {
        const element = textarea.current;
        const caret = pendingCaret.current;
        if (!element || caret === null) return;
        pendingCaret.current = null;
        element.focus();
        element.setSelectionRange(caret, caret);
    });

    const pick = (item: CompletionItem) => {
        if (!completion) return;
        const next = applyCompletion(text, completion, item);
        write(next.text, next.caret);
    };

    /** Puts `snippet` in place of the selection; the cursor goes `caret` characters into it. */
    const insert = (snippet: string, caret = snippet.length) => {
        const before = text.slice(0, selection.start);
        const after = text.slice(selection.end);
        // A space keeps the inserted piece apart from what is right before it.
        const glue = before && !/[\s([]$/.test(before) ? " " : "";
        write(before + glue + snippet + after, before.length + glue.length + caret);
        setMenu(null);
    };

    // A menu closes back on the formula, not on its button.
    const keepTextFocus = (event: Event) => {
        event.preventDefault();
        const element = textarea.current;
        if (!element) return;
        element.focus();
        element.setSelectionRange(selection.start, selection.end);
    };

    const done = () => {
        if (!current || current.status !== "valid") return;
        onDone({ canonical: current.canonical, display: current.display });
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            done();
            return;
        }
        if (showCompletion && completion) {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                const count = completion.items.length;
                setActive(index => (index + (event.key === "ArrowDown" ? 1 : count - 1)) % count);
                return;
            }
            if (event.key === "Enter" || event.key === "Tab") {
                event.preventDefault();
                pick(completion.items[Math.min(active, completion.items.length - 1)]);
                return;
            }
            if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                setDismissed(completionKey);
                return;
            }
        }
    };

    const syncSelection = () => {
        const element = textarea.current;
        if (element) setSelection({ start: element.selectionStart, end: element.selectionEnd });
    };

    const status = failed
        ? { text: "The formula could not be checked. Try again in a moment.", className: "text-red-700" }
        : !shown
          ? { text: "Checking…", className: "text-gray-500" }
          : shown.status === "valid"
            ? { text: shown.message, className: "text-green-700" }
            : shown.status === "invalid"
              ? { text: shown.message, className: "text-red-700" }
              : { text: shown.message, className: "text-gray-600" };
    const activeItem = showCompletion && completion ? completion.items[Math.min(active, completion.items.length - 1)] : null;

    return (
        <div className="flex flex-col" data-testid="formula-editor">
            <div className="flex items-baseline justify-between gap-3 px-3 pb-2 pt-3">
                <span className="text-sm font-semibold text-black">
                    {fieldLabel} {OPERATOR_LABELS[operator]}…
                </span>
                {scopes.length > 0 && <ScopesHint scopes={scopes} />}
            </div>

            <div ref={box} className={cn("relative mx-3 rounded-sm border-2 bg-white", current?.status === "invalid" ? "border-red-600" : "border-denim-blue")}>
                <div aria-hidden className={cn(BOX, "pointer-events-none min-h-[66px] text-gray-900")} data-testid="formula-highlight">
                    <FormulaText text={text} scopes={scopes} functions={functionNames} errors={errors} />
                    {/* Keeps the last line's height when the text ends with a line break. */}
                    {"​"}
                </div>
                {/* Where the completion opens: under the start of the word being typed. */}
                <div aria-hidden className={cn(BOX, "pointer-events-none invisible absolute inset-x-0 top-0")}>
                    {text.slice(0, completion?.from ?? selection.start)}
                    <span ref={marker}>{"​"}</span>
                </div>
                <textarea
                    ref={textarea}
                    value={text}
                    spellCheck={false}
                    autoComplete="off"
                    aria-label="Formula"
                    aria-invalid={current?.status === "invalid" || undefined}
                    aria-autocomplete="list"
                    aria-expanded={showCompletion}
                    aria-controls={showCompletion ? listId : undefined}
                    aria-activedescendant={activeItem ? `${listId}-${activeItem.key}` : undefined}
                    // Read by the popover around the editor: Escape closes the suggestions first.
                    data-completion-open={showCompletion || undefined}
                    placeholder={placeholderFor(fieldLabel, fieldType)}
                    className={cn(
                        BOX,
                        "absolute inset-0 h-full w-full resize-none overflow-hidden bg-transparent text-transparent caret-gray-900 outline-none selection:bg-blue-200 placeholder:text-gray-400"
                    )}
                    onChange={event => {
                        setText(event.target.value);
                        setSelection({ start: event.target.selectionStart, end: event.target.selectionEnd });
                    }}
                    onSelect={syncSelection}
                    onKeyDown={onKeyDown}
                    onKeyUp={syncSelection}
                    onClick={syncSelection}
                />
                {showCompletion && completion && (
                    <CompletionList id={listId} completion={completion} active={active} onHover={setActive} onPick={pick} style={{ top: anchor.top, left: anchor.left }} />
                )}
                <div className="flex items-center gap-2 rounded-b-sm border-t bg-gray-50 px-2.5 py-1 text-xs" aria-live="polite" data-testid="formula-status">
                    {pending && shown && <Loader className="h-3 w-3 flex-shrink-0 animate-spin text-gray-400" aria-hidden />}
                    {!pending && current?.status === "valid" && <Check className="h-3 w-3 flex-shrink-0 text-green-700" aria-hidden />}
                    <span className={cn("font-medium", status.className)}>{status.text}</span>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                <Popover open={menu === "fields"} onOpenChange={open => setMenu(open ? "fields" : null)}>
                    <PopoverTrigger asChild>
                        <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 rounded-sm text-[13px]" aria-expanded={menu === "fields"}>
                            {scopes[0] && <span className="h-3.5 w-3.5 rounded-[3px]" style={{ backgroundColor: scopes[0].color || "#6b7280" }} aria-hidden />}
                            Insert a field
                            <ChevronDown className="h-3.5 w-3.5 text-gray-500" aria-hidden />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-[360px] p-0" onCloseAutoFocus={keepTextFocus}>
                        {scopeSchemas ? <FieldMenu scopes={scopeSchemas} onPick={snippet => insert(snippet)} /> : <Loader className="m-3 h-4 w-4 animate-spin text-gray-500" />}
                    </PopoverContent>
                </Popover>
                <Popover open={menu === "functions"} onOpenChange={open => setMenu(open ? "functions" : null)}>
                    <PopoverTrigger asChild>
                        <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 rounded-sm text-[13px]" aria-expanded={menu === "functions"}>
                            <span className="font-mono text-xs font-bold leading-none text-violet-700" aria-hidden>
                                ƒ
                            </span>
                            Insert a function
                            <ChevronDown className="h-3.5 w-3.5 text-gray-500" aria-hidden />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-[400px] p-0" onCloseAutoFocus={keepTextFocus}>
                        <FunctionMenu functions={functions} onPick={item => insert(`${item.name}()`, item.signature.endsWith("()") ? item.name.length + 2 : item.name.length + 1)} />
                    </PopoverContent>
                </Popover>
                <span className="text-xs text-gray-500">Inserted at the cursor</span>
            </div>

            <div className="mx-3 mb-3 flex flex-col gap-1 rounded-sm bg-gray-100 p-2.5" data-testid="formula-sample">
                {shown?.sample ? (
                    <>
                        <span className="text-xs font-semibold text-gray-600">
                            On{" "}
                            {shown.sample.record.object_url ? (
                                <a href={`/${shown.sample.record.object_url}/${shown.sample.record.id}`} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                                    {shown.sample.record.label}
                                </a>
                            ) : (
                                shown.sample.record.label
                            )}
                        </span>
                        <span className={cn("font-mono text-xs leading-[18px] text-gray-900", !current && "opacity-60")}>{shown.sample.trace}</span>
                        {shown.sample.empty && <span className="text-xs text-gray-600">An empty result never matches: this record would not be listed by this condition.</span>}
                    </>
                ) : shown?.status === "valid" ? (
                    <span className="text-xs text-gray-600">No record to try the formula on yet.</span>
                ) : (
                    <span className="text-xs text-gray-600">Once the formula is complete, what it gives on a real record shows here.</span>
                )}
            </div>

            <div className="flex items-center justify-between gap-2 border-t px-3 py-2.5">
                <Button type="button" variant="link" className="h-8 px-0 text-blue-700" onClick={onManual}>
                    Back to a manual value
                </Button>
                <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" className="h-8" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="button" size="sm" className="h-8" disabled={!valid} onClick={done} title={valid ? "Ctrl + Enter" : undefined}>
                        Done
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ScopesHint({ scopes }: { scopes: FormulaScope[] }) {
    const named = [...scopes].reverse();
    return (
        <span className="text-right text-xs text-gray-500">
            Fields of{" "}
            {named.map((scope, index) => (
                <React.Fragment key={scope.index}>
                    {index > 0 && " or of "}
                    <strong className="font-semibold" style={{ color: fieldStyle(scope.color).color }}>
                        {describeScope(scope, scopes.length)}
                    </strong>
                </React.Fragment>
            ))}
            {scopes.length === 1 && " and of the records it links to"}
        </span>
    );
}

function Highlighted({ label, query }: { label: string; query: string }) {
    const at = query ? fold(label).lastIndexOf(fold(query)) : -1;
    if (at < 0 || !query) return <>{label}</>;
    return (
        <>
            {label.slice(0, at)}
            <strong className="font-semibold">{label.slice(at, at + query.length)}</strong>
            {label.slice(at + query.length)}
        </>
    );
}

function CompletionList({
    id,
    completion,
    active,
    onHover,
    onPick,
    style
}: {
    id: string;
    completion: Completion;
    active: number;
    onHover: (index: number) => void;
    onPick: (item: CompletionItem) => void;
    style: React.CSSProperties;
}) {
    const current = completion.items[Math.min(active, completion.items.length - 1)];
    return (
        <div
            role="listbox"
            id={id}
            aria-label="Suggestions"
            className="absolute z-20 w-[320px] overflow-hidden rounded-md border bg-white py-1 font-sans text-[13px] leading-[18px] shadow-lg"
            style={style}
            data-testid="formula-completion"
        >
            {completion.items.map((item, index) => (
                <div
                    key={item.key}
                    id={`${id}-${item.key}`}
                    role="option"
                    aria-selected={index === active}
                    className={cn("flex cursor-pointer items-center gap-2 px-2.5 py-1.5", index === active && "bg-denim-blue/10")}
                    onMouseEnter={() => onHover(index)}
                    // Keep the focus (and the cursor) in the textarea.
                    onMouseDown={event => {
                        event.preventDefault();
                        onPick(item);
                    }}
                >
                    {item.kind === "function" ? (
                        <span className="min-w-0 truncate font-mono text-xs">
                            <span className="font-bold text-violet-700">{item.label}</span>
                            <span className="text-gray-500">{item.detail}</span>
                        </span>
                    ) : (
                        <>
                            {item.scope && <span className="h-3.5 w-3.5 flex-shrink-0 rounded-[3px]" style={{ backgroundColor: item.scope.color || "#6b7280" }} aria-hidden />}
                            <span className="min-w-0 truncate text-gray-900">
                                <Highlighted label={item.label} query={completion.query} />
                                {item.continues && <ChevronRight className="ml-0.5 inline h-3 w-3 text-gray-500" aria-hidden />}
                            </span>
                        </>
                    )}
                    <span className="ml-auto flex-shrink-0 pl-2 text-xs text-gray-500">
                        {item.kind === "function" ? "Function" : item.kind === "unit" ? "Duration" : item.kind === "field" ? item.detail : ""}
                    </span>
                </div>
            ))}
            <div className="mt-1 flex flex-col gap-0.5 border-t px-2.5 pb-1 pt-1.5">
                {current?.kind === "function" && current.help && (
                    <span className="text-xs text-gray-700">
                        <span className="font-mono font-bold text-violet-700">{current.label}</span>: {current.help}
                    </span>
                )}
                <span className="text-xs text-gray-500">↑↓ choose · Tab or Enter insert · Esc close</span>
            </div>
        </div>
    );
}

/** A linked record walked into: `written` is how the formula names the relation. */
type Hop = { relation: SchemaRelation; schema: FilterSchema; written: string };

/** Every field a formula can read, by scope, through the records they link to. */
function FieldMenu({ scopes, onPick }: { scopes: { scope: FormulaScope; schema: FilterSchema }[]; onPick: (snippet: string) => void }) {
    const [hops, setHops] = useState<{ scope: FormulaScope; path: Hop[] } | null>(null);
    const [loading, setLoading] = useState(false);
    const open = async (scope: FormulaScope, path: Hop[], relation: SchemaRelation, from: FilterSchema) => {
        setLoading(true);
        try {
            const schema = await loadFilterSchema(relation.model);
            setHops({ scope, path: [...path, { relation, schema, written: shownLabel(from, relation) }] });
        } finally {
            setLoading(false);
        }
    };
    const snippet = (scope: FormulaScope, labels: string[]) => `[${[scope.name, ...labels].join(".")}]`;

    if (hops) {
        const last = hops.path[hops.path.length - 1];
        const labels = hops.path.map(hop => hop.written);
        return (
            <Command filter={containsFilter} className="max-h-[380px]">
                <div className="flex items-center gap-1 border-b px-2 py-1.5 text-xs text-gray-600">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        aria-label="Back"
                        onClick={() => setHops(hops.path.length > 1 ? { ...hops, path: hops.path.slice(0, -1) } : null)}
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <span className="truncate">{[hops.scope.name, ...hops.path.map(hop => hop.relation.label)].join(" › ")}</span>
                </div>
                <CommandInput placeholder={`Search a field of ${last.relation.label.toLowerCase()}…`} />
                <CommandList>
                    <CommandEmpty>No field.</CommandEmpty>
                    <CommandGroup>
                        {last.relation.formula_operators?.length ? (
                            <CommandItem value={`${last.relation.label} itself`} onSelect={() => onPick(snippet(hops.scope, labels))}>
                                <span className="truncate">{last.relation.label} itself</span>
                                <span className="ml-auto pl-2 text-xs text-gray-500">Record</span>
                            </CommandItem>
                        ) : null}
                        <SchemaItems
                            schema={last.schema}
                            onField={label => onPick(snippet(hops.scope, [...labels, label]))}
                            onRelation={relation => open(hops.scope, hops.path, relation, last.schema)}
                        />
                    </CommandGroup>
                </CommandList>
            </Command>
        );
    }
    return (
        <Command filter={containsFilter} className="max-h-[380px]">
            <CommandInput placeholder="Search a field…" />
            <CommandList>
                <CommandEmpty>No field.</CommandEmpty>
                {loading && <Loader className="m-3 h-4 w-4 animate-spin text-gray-500" />}
                {scopes.map(({ scope, schema }) => (
                    <CommandGroup
                        key={scope.index}
                        heading={
                            <span className="flex items-center gap-1.5">
                                <FilterSchemaTile schema={schema} />
                                <span>{capitalize(describeScope(scope, scopes.length))}</span>
                            </span>
                        }
                    >
                        <SchemaItems schema={schema} prefix={scope.name} onField={label => onPick(snippet(scope, [label]))} onRelation={relation => open(scope, [], relation, schema)} />
                    </CommandGroup>
                ))}
            </CommandList>
        </Command>
    );
}

function SchemaItems({ schema, prefix = "", onField, onRelation }: { schema: FilterSchema; prefix?: string; onField: (label: string) => void; onRelation: (relation: SchemaRelation) => void }) {
    return (
        <>
            {formulaFields(schema).map(field => (
                <CommandItem key={`f-${field.name}`} value={`${prefix} ${field.label} ${field.name}`} onSelect={() => onField(shownLabel(schema, field))}>
                    <span className="truncate">{field.label}</span>
                    <span className="ml-auto pl-2 text-xs text-gray-500">{TYPE_LABELS[field.type] ?? ""}</span>
                </CommandItem>
            ))}
            {formulaRelations(schema).map(relation => (
                <CommandItem key={`r-${relation.name}`} value={`${prefix} ${relation.label} ${relation.name} ›`} onSelect={() => onRelation(relation)}>
                    <span className="truncate">{relation.label}</span>
                    <ChevronRight className="ml-auto h-4 w-4 flex-shrink-0 text-gray-500" aria-label="Fields of" />
                </CommandItem>
            ))}
        </>
    );
}

const TYPE_LABELS: Record<string, string> = { text: "Text", choice: "Choice", picklist: "Choice", number: "Number", date: "Date", datetime: "Date & time" };

/** The functions of the formula language, by group, with what each takes and gives. */
function FunctionMenu({ functions, onPick }: { functions: FormulaFunction[]; onPick: (item: FormulaFunction) => void }) {
    const groups = functions.reduce<Record<string, FormulaFunction[]>>((all, item) => ({ ...all, [item.group]: [...(all[item.group] ?? []), item] }), {});
    if (!functions.length) return <Loader className="m-3 h-4 w-4 animate-spin text-gray-500" />;
    return (
        <Command filter={containsFilter} className="max-h-[380px]">
            <CommandInput placeholder="Search a function…" />
            <CommandList>
                <CommandEmpty>No function.</CommandEmpty>
                {Object.entries(groups).map(([group, items]) => (
                    <CommandGroup key={group} heading={group}>
                        {items.map(item => (
                            <CommandItem key={item.name} value={`${item.name} ${item.help}`} onSelect={() => onPick(item)} className="flex-col items-start gap-0">
                                <span className="font-mono text-xs">
                                    <span className="font-bold text-violet-700">{item.name}</span>
                                    <span className="text-gray-500">{item.signature.slice(item.name.length)}</span>
                                </span>
                                <span className="text-xs text-gray-600">{item.help}</span>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                ))}
            </CommandList>
        </Command>
    );
}
